/* src/pages/CreateQuote.jsx */
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Center,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../components/CreateQuote/HeaderBar";
import SummaryBox from "../components/CreateQuote/SummaryBox";
import ModelSelector from "../components/CreateQuote/ModelSelector";
import PeriodicitySelector from "../components/CreateQuote/PeriodicitySelector";
import ProductFilters from "../components/CreateQuote/ProductFilters";
import ProductsTable from "../components/CreateQuote/ProductsTable";
import OptionsTable from "../components/CreateQuote/OptionsTable";
import QuoteFooter from "../components/CreateQuote/QuoteFooter";

import {
  fetchModels,
  fetchPeriodicities,
  fetchSlas,
  fetchFilteredProducts,
  recalculateProduct,
  createQuote,
  createQuoteProduct,
} from "../services/api";

import { useAuth } from "../hooks/useAuth";
import useEntryLevelPrice from "../hooks/useEntryLevelPrice";
import usePaginatedProducts from "../hooks/usePaginatedProducts";

import {
  normalizePage,
  extractDynamicFilters,
  filterAndPaginate,
  calcSummary,
} from "../services/quoteHelpers";

/* chiave localStorage per lo switch “Versione cliente” */
const CUSTOMER_VERSION_KEY = "customerVersion";

/* hook per ottenere lo sconto percentuale della periodicità corrente */
const usePeriodicityDiscount = (period) =>
  period ? Number(period.discount ?? 0) : 0;

export default function CreateQuote() {
  const toast = useToast();
  const navigate = useNavigate();
  const { customer, user } = useAuth();

  /* -------------------- stato base -------------------- */
  const [initLoading, setInitLoading] = useState(true);
  const [fullListLoading, setFullListLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [waitingNewPage, setWaitingNewPage] = useState(false);

  const [customerVersion, setCustomerVersion] = useState(
    JSON.parse(localStorage.getItem(CUSTOMER_VERSION_KEY)) || false
  );

  /* step modello / periodicità */
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [periodicities, setPeriodicities] = useState([]);
  const [selectedPeriodicity, setSelectedPeriodicity] = useState(null);
  const [slas, setSlas] = useState([]);

  /* prodotti */
  const [allProducts, setAllProducts] = useState([]);
  const [pageProducts, setPageProducts] = useState([]);
  useEntryLevelPrice(setPageProducts);

  /* filtri UI */
  const [filters, setFilters] = useState({
    brand_id: "", categories_id: "", subcategory_id: "", family_id: "",
  });
  const [searchText, setSearchText] = useState("");
  const [productCode, setProductCode] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  /* meta */
  const [note, setNote] = useState("");
  const [totalProducts, setTotalProducts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  /* filtri dinamici */
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [families, setFamilies] = useState([]);

  /* sconto periodo corrente – serve alla tabella */
  const periodicityDiscount = usePeriodicityDiscount(selectedPeriodicity);

  /* -------------------------------------------------- */
  /* cambio “prodotti selezionati” ⇒ pagina 1           */
  useEffect(() => { setCurrentPage(1); }, [showSelectedOnly]);

  /* evita pagine vuote                                 */
  useEffect(() => {
    const last = Math.max(1, Math.ceil(totalProducts / itemsPerPage));
    if (currentPage > last) setCurrentPage(last);
  }, [totalProducts, itemsPerPage, currentPage]);

  /* ---------------- dati statici -------------------- */
  useEffect(() => {
    (async () => {
      try {
        const [m, p, s] = await Promise.all([
          fetchModels(),
          fetchPeriodicities(),
          fetchSlas(),
        ]);
        setModels(Array.isArray(m) ? m : m.data || []);
        setPeriodicities(Array.isArray(p) ? p : p.data || []);
        setSlas(Array.isArray(s) ? s : s.data || []);
      } catch (err) {
        toast({ title: "Errore iniziale", description: err.message, status: "error" });
      } finally { setInitLoading(false); }
    })();
  }, [toast]);

  /* ------------- query pagina filtrata -------------- */
  const paginatedParams = {
    model_id: selectedModel?.id,
    periodicity_id: selectedPeriodicity?.id,
    customer_id: customer?.id,
    ...(customerVersion && { customer_version: 1 }),
    brand_id: filters.brand_id || undefined,
    categories_id: filters.categories_id || undefined,
    subcategory_id: filters.subcategory_id || undefined,
    family_id: filters.family_id || undefined,
    page: currentPage,
    per_page: itemsPerPage,
  };

  const {
    data: qpData,
    isFetching,
    isPlaceholderData,
    error: qpError,
  } = usePaginatedProducts(paginatedParams);

  const productsLoading = (isFetching && !qpData) || waitingNewPage;
  useEffect(() => {
    if (!qpData || isPlaceholderData) return;

    const normalized = normalizePage(
      Array.isArray(qpData.data) ? qpData.data : qpData.data?.data || [],
      allProducts,
      slas
    );

    setPageProducts(normalized);
    if (allProducts.length === 0) setAllProducts(normalized);

    setWaitingNewPage(false);

    setTotalProducts(qpData.total ?? qpData.data?.total ?? 0);
    const dyn = extractDynamicFilters(normalized);
    setBrands((prev) => [...prev, ...dyn.brands].filter((v, i, a) => a.findIndex(x => x.id === v.id) === i));
    setCategories((prev) => [...prev, ...dyn.categories].filter((v, i, a) => a.findIndex(x => x.id === v.id) === i));
    setSubCategories((prev) => [...prev, ...dyn.subCategories].filter((v, i, a) => a.findIndex(x => x.id === v.id) === i));
    setFamilies((prev) => [...prev, ...dyn.families].filter((v, i, a) => a.findIndex(x => x.id === v.id) === i));
  }, [qpData, isPlaceholderData, slas]);

  useEffect(() => {
    if (qpError) {
      toast({ title: "Errore prodotti", description: qpError.message, status: "error" });
    }
  }, [qpError, toast]);

  /* ------------- lista completa (ricerche) ---------- */
  useEffect(() => {
    if (!selectedModel || !selectedPeriodicity || !customer?.id) return;

    setFullListLoading(true);
    (async () => {
      try {
        let all = [];
        let page = 1;
        let hasMore = true;

        while (hasMore && page <= 50) {
          const resp = await fetchFilteredProducts({
            model_id: selectedModel.id,
            periodicity_id: selectedPeriodicity.id,
            customer_id: customer.id,
            ...(customerVersion && { customer_version: 1 }),
            page,
            per_page: 100,
          });
          const data = Array.isArray(resp.data)
            ? resp.data
            : resp.data?.data || [];
          all = all.concat(data);
          const total = resp.total ?? resp.data?.total ?? 0;
          hasMore = page * 100 < total;
          page += 1;
        }

        const normalized = normalizePage(all, [], slas);
        setAllProducts(normalized);

        const dyn = extractDynamicFilters(normalized);
        setBrands(dyn.brands);
        setCategories(dyn.categories);
        setSubCategories(dyn.subCategories);
        setFamilies(dyn.families);
      } catch (err) {
        toast({ title: "Errore ricerca", description: err.message, status: "error" });
      } finally {
        setFullListLoading(false);
      }
    })();
  }, [
    selectedModel?.id,
    selectedPeriodicity?.id,
    customer?.id,
    customerVersion,
    slas,
  ]);

  /* ------------- handlers modello / periodicità ----- */
  const selectModel = (m) => {
    setSelectedModel(m);
    setWaitingNewPage(true);
    setPageProducts([]);
    setAllProducts([]);
    setFilters({ brand_id: "", categories_id: "", subcategory_id: "", family_id: "" });
    setCurrentPage(1);
  };

  const selectPeriodicity = (p) => {
    setSelectedPeriodicity(p);
    setWaitingNewPage(true);
    setPageProducts([]);
    setAllProducts([]);
    setCurrentPage(1);
  };

  /* ------------- toggle “Versione cliente” ---------- */
  const toggleCustomerVersion = () => {
    const next = !customerVersion;
    setCustomerVersion(next);
    localStorage.setItem(CUSTOMER_VERSION_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("customerVersionChanged", { detail: next }));

    setWaitingNewPage(true);
    setPageProducts([]);
    setAllProducts([]);
    setCurrentPage(1);
  };

  /* ------------- filtro + sort + paginazione -------- */
  const { total, page: displayedProducts } = useMemo(() =>
    filterAndPaginate({
      list: allProducts,
      filters,
      productCode,
      searchText,
      showSelectedOnly,
      currentPage,
      itemsPerPage,
    }),
    [
      allProducts,
      filters,
      productCode,
      searchText,
      showSelectedOnly,
      currentPage,
      itemsPerPage,
    ]
  );

  useEffect(() => {
    if (!waitingNewPage) setTotalProducts(total);
  }, [total, waitingNewPage]);

  /* ------------- PATCH & RECALC ---------------------- */
  /* ---------- PATCH & RECALC ------------------------------------ */
  const patchCollections = (code, patch) => {
    setPageProducts((prev) =>
      prev.map((x) => (x.product_code === code ? { ...x, ...patch } : x)));
    setAllProducts((prev) =>
      prev.map((x) => (x.product_code === code ? { ...x, ...patch } : x)));
  };

  /* 🔧 nuova versione – usa *tutti* i valori restituiti dal backend */
  const recalc = async (code, qty, slaId) => {
    try {
      const { data } = await recalculateProduct({
        product_code: code,
        quantity: Number(qty),
        customer_id: customer.id,
        periodicity_months: selectedPeriodicity.duration_months,
        sla_id: slaId,
        ...(customerVersion && { customer_version: 1 }),
      });

      /* normalizzo i campi che servono alla tabella */
      const listino = Number(data.price_per_month ?? 0);
      const riservato = Number(data.reserved_per_month ?? 0);
      const totListino = Number(data.totale ?? 0);
      const totRiserv = Number(data.totale_scontato ?? 0);
      const scontoPc = listino > 0 ? ((1 - riservato / listino) * 100) : 0;

      patchCollections(code, {
        quantity: Number(qty),
        sla_id: slaId,
        price_per_month: listino,
        reserved_per_month: riservato,
        price_reserved: riservato,     // ← serve al salvataggio nel footer
        totale: totListino,
        totale_scontato: totRiserv,
        discount_percent: scontoPc,
      });
    } catch (err) {
      console.error("Errore recalc:", err);
    }
  };


  const handleQty = (code, rawValue, slaId) => {
    const qty = rawValue === "" ? "" : Number(rawValue);
    patchCollections(code, { quantity: qty });
    if (qty > 0) recalc(code, qty, slaId);
  };

  const handleSla = (code, slaId, qty) => {
    patchCollections(code, { sla_id: slaId });
    recalc(code, qty, slaId);
  };

  const toggleSelect = (code) => {
    patchCollections(
      code,
      { selected: !allProducts.find((p) => p.product_code === code)?.selected }
    );
  };

  /* ------------- riepilogo --------------------------- */
  const selectedProducts = useMemo(
    () => allProducts.filter((p) => p.selected),
    [allProducts]
  );

  const summary = useMemo(
    () => calcSummary(selectedProducts, selectedPeriodicity),
    [selectedProducts, selectedPeriodicity]
  );

  /* ------------- RENDER ------------------------------ */
  if (initLoading) {
    return (
      <Center h="100vh" flexDirection="column">
        <Spinner size="xl" mb={4} />
        <Text>Caricamento iniziale…</Text>
      </Center>
    );
  }

  return (
    <Box pt={{ base: 6, md: 8 }} px={{ base: 4, md: 6 }}>
      <HeaderBar
        customerVersion={customerVersion}
        onToggleCustomerVersion={toggleCustomerVersion}
      />

      <ModelSelector
        models={models}
        selected={selectedModel}
        onSelect={selectModel}
      />

      {selectedModel && (
        <PeriodicitySelector
          periodicities={periodicities}
          selected={selectedPeriodicity}
          onSelect={selectPeriodicity}
        />
      )}

      {selectedPeriodicity && (
        <>
          <ProductFilters
            filters={filters}
            brands={brands}
            categories={categories}
            subCategories={subCategories}
            families={families}
            productCode={productCode}
            searchText={searchText}
            itemsPerPage={itemsPerPage}
            showSelectedOnly={showSelectedOnly}
            totalProducts={totalProducts}
            onFilterChange={setFilters}
            onSearchTextChange={setSearchText}
            onCodeChange={setProductCode}
            onPageSizeChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            onShowSelectedToggle={() => setShowSelectedOnly((prev) => !prev)}
            onReset={() => {
              setFilters({ brand_id: "", categories_id: "", subcategory_id: "", family_id: "" });
              setSearchText(""); setProductCode(""); setCurrentPage(1);
            }}
          />

          <ProductsTable
            products={productsLoading ? [] : displayedProducts}
            slas={slas}
            showSlaColumn={selectedModel?.isSla !== 0}
            currentPage={currentPage}
            totalProducts={totalProducts}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onToggleSelect={toggleSelect}
            onQuantityChange={handleQty}
            onSlaChange={handleSla}
            productsLoading={productsLoading}
            periodicityDiscount={periodicityDiscount}   /* ← nuova prop */
          />
        </>
      )}

      {selectedProducts.length > 0 && (
        <OptionsTable
          rows={selectedProducts.flatMap((prod) =>
            (prod.options || []).map((o) => ({
              parentCode: prod.product_code,
              product_code: o.product_code_option,
              description: o.description_option,
              price_per_month: (o.price_per_month_option ?? 0) * prod.quantity,
            }))
          )}
        />
      )}

      {selectedProducts.length > 0 && (
        <QuoteFooter
          note={note}
          onNoteChange={setNote}
          isSaving={isSaving}
          onSave={async () => {
            if (selectedProducts.length === 0) {
              toast({ title: "Seleziona almeno un prodotto", status: "warning" });
              return;
            }

            setIsSaving(true);
            try {
              const { data: newQuote } = await createQuote({
                customer_id: customer.id,
                user_id: user.id,
                model_id: selectedModel.id,
                periodicity_id: selectedPeriodicity.id,
                note,
                discount_extra: 0,
                date: new Date().toISOString().slice(0, 10),
                total_amount: summary.totale,
                quote_status_id: 1,
              });

              for (const p of selectedProducts) {
                await createQuoteProduct({
                  quote_id: newQuote.id,
                  pivot_id: p.pivot_id,
                  product_id: p.product_code,
                  description: p.description,
                  extended_description: p.extended_description,
                  quantity: p.quantity,
                  sla_id: p.sla_id,
                  version: p.version,
                  last_unit_price: p.price_per_month,
                  price_reserved: p.price_reserved,
                  discount_percent: periodicityDiscount,
                  total_amount: p.totale,
                  total_amount_scontato: p.totale_scontato,
                  product_datasheet: p.product_datasheet,
                  brand_id: p.brand.id,
                  category_id: p.category.id,
                  family_id: p.family.id,
                });

                for (const o of p.options || []) {
                  await createQuoteProduct({
                    quote_id: newQuote.id,
                    product_id: o.product_code_option,
                    description: o.description_option,
                    quantity: p.quantity,
                    sla_id: o.sla_id_option || null,
                    version: o.version_option || 1,
                    last_unit_price: o.price_per_month_option,
                    price_reserved: o.reserved_per_month_option || 0,
                    discount_percent: periodicityDiscount,
                    total_amount: o.totale_option,
                    total_amount_scontato: o.totale_scontato_option,
                  });
                }
              }

              toast({ title: "Preventivo creato con successo", status: "success" });
              navigate("/quotes");
            } catch (err) {
              toast({ title: "Errore creazione preventivo", description: err.message, status: "error" });
            } finally { setIsSaving(false); }
          }}
        />
      )}

      {summary && <SummaryBox summary={summary} />}
    </Box>
  );
}
